import { useEffect, useState } from "react";
import { Loader2, Sparkles, BookHeart, Lock, Laugh, Smile, Meh, Moon, Frown } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { MoodChart } from "@/components/MoodChart";

export const MOODS = [
  ["super", "Super", "bg-rose-500", Laugh], ["gut", "Gut", "bg-pink-400", Smile], ["okay", "Okay", "bg-amber-300", Meh],
  ["muede", "Müde", "bg-sky-300", Moon], ["traurig", "Traurig", "bg-slate-400", Frown],
];
const label = (k) => MOODS.find((m) => m[0] === k);
const fmt = (d) => new Date(d).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" });

export function Barometer({ b, testId = "mood-barometer" }) {
  const top = b.total ? MOODS.reduce((a, m) => (b.counts[m[0]] > b.counts[a[0]] ? m : a), MOODS[0]) : null;
  return (
    <div className="space-y-3" data-testid={testId}>
      <p className="text-sm text-slate-500">{b.total ? <>Heute haben <b data-testid="mood-total">{b.total}</b> Mitglieder ihre Stimmung geteilt – die Community fühlt sich vor allem <b className="text-rose-600">{top[1]}</b>.</> : "Heute hat noch niemand seine Stimmung geteilt."}</p>
      <div className="space-y-2">
        {MOODS.map(([k, l, c, Ic]) => {
          const pct = b.total ? Math.round((b.counts[k] / b.total) * 100) : 0;
          return (
            <div key={k} className="flex items-center gap-3" data-testid={`mood-bar-${k}`}>
              <span className="w-20 text-sm font-semibold shrink-0 flex items-center gap-1.5"><Ic className="w-4 h-4 text-rose-500" /> {l}</span>
              <div className="flex-1 h-3 rounded-full bg-rose-50 overflow-hidden"><div className={`h-full ${c} rounded-full transition-[width] duration-700`} style={{ width: `${pct}%` }} /></div>
              <span className="w-10 text-right text-xs font-bold text-slate-500">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Mood() {
  const [d, setD] = useState(null);
  const [diary, setDiary] = useState([]);
  const [mood, setMood] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([api.get("/mood"), api.get("/mood/diary")]).then(([a, b]) => {
    setD(a.data); setDiary(b.data);
    if (a.data.mine) { setMood(a.data.mine.mood); setNote(a.data.mine.note || ""); }
  });
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/mood", { mood, note });
      toast.success(data.awarded ? `Gespeichert – +${data.awarded} Punkte` : "Stimmung aktualisiert");
      await load();
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  if (!d) return <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-rose-500" /></div>;
  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="mood-page">
      <div>
        <p className="text-xs tracking-wider uppercase font-semibold text-rose-600">Tagebuch & Stimmung</p>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Wie geht's dir heute?</h1>
        <p className="text-sm text-slate-500 mt-1">Deine Stimmung zählt anonym fürs Community-Barometer. Dein Tagebuch-Eintrag bleibt privat. Für den ersten Eintrag am Tag gibt's {d.points} Punkte.</p>
      </div>
      <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-4">
        <div className="grid grid-cols-5 gap-2" role="radiogroup">
          {MOODS.map(([k, l, , Ic]) => (
            <button key={k} role="radio" aria-checked={mood === k} onClick={() => setMood(k)} data-testid={`mood-pick-${k}`}
              className={`rounded-2xl py-3 flex flex-col items-center gap-1 border transition-[transform,background-color,border-color] active:scale-95 ${mood === k ? "bg-rose-50 border-rose-400 scale-105" : "border-pink-100 hover:bg-rose-50/50"}`}>
              <Ic className={`w-8 h-8 ${mood === k ? "text-rose-500" : "text-slate-400"}`} /><span className="text-xs font-bold">{l}</span>
            </button>
          ))}
        </div>
        <div>
          <p className="text-sm font-semibold flex items-center gap-1.5 mb-1.5"><Lock className="w-3.5 h-3.5 text-slate-400" /> Mein Tagebuch (nur für dich)</p>
          <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} rows={3} placeholder="Was war heute schön, was hat dich beschäftigt?" data-testid="mood-note" className="rounded-2xl border-pink-100" />
        </div>
        <button onClick={save} disabled={!mood || busy} data-testid="mood-save" className="w-full h-12 rounded-full pink-gradient text-white font-bold flex items-center justify-center gap-2 disabled:opacity-50 active:scale-[.98] transition-transform">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {d.mine ? "Aktualisieren" : "Stimmung teilen"}
        </button>
      </div>
      <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-3">
        <h2 className="font-display text-xl font-extrabold">Community-Barometer heute</h2>
        <Barometer b={d.barometer} />
      </div>
      <MoodChart diary={diary} />
      <div className="bg-white rounded-3xl border border-pink-100 p-5 sm:p-6 space-y-3" data-testid="mood-diary">
        <h2 className="font-display text-xl font-extrabold flex items-center gap-2"><BookHeart className="w-5 h-5 text-rose-500" /> Mein Tagebuch</h2>
        {diary.length === 0 ? <p className="text-sm text-slate-400">Noch keine Einträge.</p> : (
          <ul className="space-y-2">
            {diary.map((x) => (
              <li key={x.date} className="flex gap-3 p-3 rounded-2xl bg-rose-50/50" data-testid={`mood-diary-${x.date}`}>
                {(() => { const Ic = label(x.mood)?.[3] || Smile; return <Ic className="w-6 h-6 text-rose-500 shrink-0" />; })()}
                <div className="min-w-0"><p className="text-xs font-bold text-slate-500">{fmt(x.date)} · {label(x.mood)?.[1]}</p>{x.note && <p className="text-sm mt-0.5 break-words">{x.note}</p>}</div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
